import React, { useState } from "react";
import { Box, Modal, Typography, IconButton, Alert } from "@mui/material";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LoadingAnimation from "../Animations/LoadingAnimation";
import { headingFonts, noOutline } from "../../Styles/Styles";

const ContactInfoModal = ({ open, onClose, serviceProviderId }) => {
  const [isPhoneRevealed, setIsPhoneRevealed] = useState(false);
  const [isEmailRevealed, setIsEmailRevealed] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleShowPhoneNumber = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/profile/serviceprovider/phoneNumber/${serviceProviderId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (response.ok) {
        setPhoneNumber(data.phoneNumber);
        setIsPhoneRevealed(true);
      } else {
        setError(data.message || "Failed to load phone number.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleShowEmail = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/profile/serviceprovider/email/${serviceProviderId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (response.ok) {
        setEmail(data.email);
        setIsEmailRevealed(true);
      } else {
        setError(data.message || "Failed to load email.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, ...headingFonts }}>
          Contact Information
        </Typography>
        {/* Phone Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#DDECF7",
            padding: "10px 15px",
            borderRadius: "50px",
            gap: 2,
            width: "100%",
          }}
        >
          <IconButton disabled sx={{ backgroundColor: "white !important" }}>
            <LocalPhoneOutlinedIcon
              fontSize="small"
              sx={{ color: "var(--primary-color)" }}
            />
          </IconButton>
          <Typography
            sx={{
              ...headingFonts,
              color: "var(--primary-color)",
            }}
          >
            {isPhoneRevealed
              ? `${phoneNumber.countryCode} ${phoneNumber.phoneNumber}`
              : "+1345*******"}
          </Typography>
          <IconButton
            onClick={handleShowPhoneNumber}
            disabled={isPhoneRevealed}
            sx={{
              backgroundColor: "var(--primary-color)",
              "&:hover": {
                backgroundColor: "var(--primary-color)", // Hover background color (optional)
              },
              ...noOutline,
            }}
          >
            {!isPhoneRevealed && (
              <VisibilityOutlinedIcon sx={{ color: "white" }} />
            )}
          </IconButton>
        </Box>
        {/* Email Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#DDECF7",
            padding: "10px 15px",
            borderRadius: "50px",
            gap: 2,
            width: "100%",
            mt: 2,
          }}
        >
          <IconButton disabled sx={{ backgroundColor: "white !important" }}>
            <EmailOutlinedIcon sx={{ color: "var(--primary-color)" }} />
          </IconButton>
          <Typography
            sx={{
              ...headingFonts,
              color: "var(--primary-color)",
            }}
          >
            {isEmailRevealed ? email : "******@email.com"}
          </Typography>
          <IconButton
            onClick={handleShowEmail}
            disabled={isEmailRevealed}
            sx={{
              backgroundColor: "var(--primary-color)",
              "&:hover": {
                backgroundColor: "var(--primary-color)", // Hover background color (optional)
              },
              ...noOutline,
            }}
          >
            {!isEmailRevealed && (
              <VisibilityOutlinedIcon sx={{ color: "white" }} />
            )}
          </IconButton>
        </Box>
        {loading && <LoadingAnimation />}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    </Modal>
  );
};

export default ContactInfoModal;
